/**
 * SEO Keywords and Structured Data Constants
 *
 * Centralized keywords for consistent SEO across the site.
 * Keywords are lowercase, deduplicated, and avoid stuffing.
 *
 * ─────────────────────────────────────────────────────────────
 * BUSINESS FOCUS (refocused 2026-07-20)
 * The line is now: LARGE OBLIGATE DART FROGS (genus Oophaga —
 * sylvatica, histrionica) + HIGH-END GECKOS (Rhacodactylus leachianus
 * "Yate", Black Night leopard geckos, Korean-line Mandarin leopard
 * geckos). "Large" and "obligate" are deliberate differentiators —
 * almost no one specializes here, unlike the thumbnail-frog shops.
 *
 * TRANSITIONAL species (Dendrobates tinctorius, Phyllobates terribilis,
 * Theloderma mossy frogs, Bombina fire-belly toads, Agalychnis tree
 * frogs) are LEGACY inventory being sold through — target removal END
 * OF 2026. Kept for now to (a) sell remaining stock and (b) capture
 * high-volume search terms (esp. "dendrobates tinctorius") and cross-
 * sell that demand into the obligate line. When a species sells out,
 * 301-redirect its care-guide page to the current focus rather than
 * deleting it (preserves SEO equity).
 * ─────────────────────────────────────────────────────────────
 */

import { formatLocality } from '../utils/scientificName';

/**
 * SEO Feature Flags
 */

// Enable Product schema offers for non-live-animal products (plants, supplies)
// Set to true when adding vivarium supplies with prices
export const ENABLE_PRODUCT_OFFERS = false;

// ═══════════════════════════════════════════════════════════════
// HERO KEYWORDS — the current focus. These lead every relevant page.
// ═══════════════════════════════════════════════════════════════

// Large obligate dart frogs (Oophaga). The signature line.
export const OOPHAGA_KEYWORDS = [
  "large obligate dart frogs",
  "obligate dart frogs",
  "large poison dart frogs",
  "oophaga",
  "oophaga sylvatica",
  "oophaga histrionica",
  "harlequin poison frog",
  "diablito poison frog",
  "large dart frogs for sale",
  "captive-bred oophaga",
  "live arrival guarantee",
];

// Select geckos — Rhacodactylus leachianus (Yate) + premium leopard
// gecko morphs (Black Night, Korean-line Mandarin).
export const LEACHIANUS_KEYWORDS = [
  "rhacodactylus leachianus",
  "leachianus gecko",
  "yate leachianus",
  "new caledonian giant gecko",
  "giant gecko for sale",
  "leachie gecko",
  "captive-bred leachianus",
];

export const LEOPARD_GECKO_KEYWORDS = [
  "leopard geckos for sale",
  "black night leopard gecko",
  "korean line mandarin leopard gecko",
  "mandarin leopard gecko",
  "high-end leopard gecko morphs",
  "designer leopard geckos",
  "captive-bred leopard geckos",
];

// Combined gecko keyword set (used by GeckosPage). NOTE: crested geckos
// intentionally REMOVED 2026-07-20 — off-focus.
export const GECKOS_KEYWORDS = [
  ...LEACHIANUS_KEYWORDS,
  ...LEOPARD_GECKO_KEYWORDS,
  "geckos for sale",
  "captive bred geckos",
];

// ═══════════════════════════════════════════════════════════════
// TRANSITIONAL KEYWORDS — legacy inventory, REMOVE end of 2026.
// Delete this whole block when the last of the stock sells through.
// ═══════════════════════════════════════════════════════════════
export const TRANSITIONAL_KEYWORDS = [
  "dendrobates tinctorius",   // highest-volume dart-frog term; cross-sell driver
  "dendrobates",
  "tinctorius dart frog",
  "phyllobates terribilis",
  "golden poison frog",
  "mossy frogs",
  "theloderma",
  "fire-bellied toads",
  "bombina orientalis",
  "tree frogs",
];

// ═══════════════════════════════════════════════════════════════
// SHARED SETS (backward-compatible export names)
// ═══════════════════════════════════════════════════════════════

// Full keyword set for comprehensive pages (home, about, frogs hub).
// Hero first, transitional appended while inventory lasts.
export const FULL_KEYWORDS = [
  ...OOPHAGA_KEYWORDS,
  "poison dart frogs",
  "dart frogs",
  ...TRANSITIONAL_KEYWORDS,
  "captive-bred frogs",
  "frog care guides",
];

// Core frog keywords for frog product/listing pages.
export const FROG_KEYWORDS = [
  ...OOPHAGA_KEYWORDS,
  "poison dart frogs",
  "dart frogs",
  "captive-bred frogs",
];

// Plants — "coming soon" supporting line (kept in nav; light keywords).
export const PLANT_KEYWORDS = [
  "vivarium plants",
  "terrarium plants",
  "bioactive vivarium plants",
  "dart frog vivarium plants",
  "live cultures",
];

// Core minimal keywords for policy pages — lead with the focus.
export const CORE_KEYWORDS = [
  "large obligate dart frogs",
  "poison dart frogs",
  "leopard geckos",
  "live arrival guarantee",
];

/**
 * JSON-LD Structured Data Templates
 */

// Organization data for homepage/main pages. Online, ships nationwide —
// so Organization (not LocalBusiness; there's no walk-in storefront).
// Description + areaServed refocused 2026-07-20.
export const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Feeling Froggy LLC",
  "url": "https://feelingfroggy.llc",
  "logo": "https://feelingfroggy.llc/logos/ff_original_96px.png",
  "sameAs": [
    "https://www.facebook.com/profile.php?id=100094008310076",
    "https://morphmarket.com/stores/allenlucke/"
  ],
  "areaServed": {
    "@type": "Country",
    "name": "United States"
  },
  "description": "Specialty breeder of large obligate dart frogs (Oophaga sylvatica, Oophaga histrionica) and select geckos — Rhacodactylus leachianus, Black Night and Korean-line Mandarin leopard geckos. Captive-bred, live-arrival guaranteed, with expert species care guides."
};

// WebSite with SearchAction for homepage
export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Feeling Froggy",
  "url": "https://feelingfroggy.llc",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://feelingfroggy.llc/frogs?search={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
};

/**
 * Genus-Specific Keywords
 */

// Oophaga (large obligate dart frogs — the hero genus)
export const OOPHAGA_GENUS_KEYWORDS = OOPHAGA_KEYWORDS;

// ── Transitional genus sets (legacy inventory, remove end 2026) ──

// Bombina (Oriental Fire-Bellied Toads)
export const BOMBINA_KEYWORDS = [
  "bombina",
  "bombina orientalis",
  "oriental fire-bellied toad",
  "fire-bellied toads",
  "captive-bred toads",
  "live arrival guarantee"
];

// Dendrobates (Dart Frogs) — highest-volume search term; cross-sell lever
export const DENDROBATES_KEYWORDS = [
  "dendrobates",
  "dendrobates tinctorius",
  "poison dart frogs",
  "dart frogs",
  "tinc dart frogs",
  "azureus dart frog",
  "captive-bred frogs",
  "live arrival guarantee"
];

// Phyllobates (Golden Poison Frogs)
export const PHYLLOBATES_KEYWORDS = [
  "phyllobates",
  "phyllobates terribilis",
  "golden poison frog",
  "poison dart frogs",
  "dart frogs",
  "terribilis",
  "orange blackfoot",
  "captive-bred frogs",
  "live arrival guarantee"
];

/**
 * Helper function to get genus-specific keywords
 */
export const getGenusKeywords = (genus) => {
  const genusLower = genus?.toLowerCase();
  switch (genusLower) {
    case 'oophaga':
      return OOPHAGA_KEYWORDS;
    case 'rhacodactylus':
      return LEACHIANUS_KEYWORDS;
    case 'bombina':
      return BOMBINA_KEYWORDS;
    case 'dendrobates':
      return DENDROBATES_KEYWORDS;
    case 'phyllobates':
      return PHYLLOBATES_KEYWORDS;
    // Gecko genera (S3-01.3 follow-up 2026-08-30): these fell to the
    // dart-frog default and shipped frog keywords in gecko page heads.
    case 'eublepharis':
      return [
        "leopard gecko for sale",
        "eublepharis macularius",
        "black night leopard gecko",
        "mandarin leopard gecko",
        "captive-bred leopard geckos",
        "leopard gecko morphs",
        "live arrival guarantee"
      ];
    case 'correlophus':
      return [
        "crested gecko for sale",
        "correlophus ciliatus",
        "captive-bred crested geckos",
        "crested gecko morphs",
        "live arrival guarantee"
      ];
    case 'mniarogekko':
      return [
        "chahoua gecko for sale",
        "mniarogekko chahoua",
        "mossy prehensile-tailed gecko",
        "captive-bred chahoua",
        "live arrival guarantee"
      ];
    default:
      // Fallback to generic keywords with genus name
      return [
        "poison dart frogs",
        "dart frogs",
        genus,
        `${genus} dart frogs`,
        `${genus} for sale`,
        "captive-bred frogs",
        "live arrival guarantee"
      ];
  }
};

/**
 * Product JSON-LD Schema Generator
 *
 * For live animals: omits "offers" to prevent Google Merchant Center auto-ingestion
 * while keeping product indexed in Search. UI/sitemap unchanged.
 */
export const createProductSchema = ({
  productId,
  name,
  commonName,
  scientificName,
  locality,
  imageUrl,
  price,
  isAvailable,
  genus,
  speciesType,
  isLiveAnimal = false
}) => {
  const baseSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": commonName || name || scientificName || "Frog",
    "description": locality
      // The ONE locality form (operator ruling 2026-10-10) — a locality
      // that already carries quotes is never wrapped a second time.
      ? `${scientificName || name} ${formatLocality(locality, speciesType)} - Captive-bred with live arrival guarantee`
      : `${scientificName || name} - Captive-bred with live arrival guarantee`,
    "image": imageUrl || "https://feelingfroggy.llc/logos/ff_original_96px.png",
    "url": `https://feelingfroggy.llc/frogs/products/${productId}`,
    "sku": productId,
    "brand": {
      "@type": "Brand",
      "name": "Feeling Froggy LLC"
    },
    "category": "Pets > Reptiles & Amphibians",
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Genus",
        "value": genus || "Unknown"
      },
      {
        "@type": "PropertyValue",
        "name": "Breeding",
        "value": "Captive-Bred"
      }
    ]
  };

  // GSC hotfix: Always include "offers" for Product schema validation
  // Google Search Console requires either offers, review, or aggregateRating
  // We include offers to satisfy this requirement for all products
  baseSchema.offers = {
    "@type": "Offer",
    "url": `https://feelingfroggy.llc/frogs/products/${productId}`,
    "priceCurrency": "USD",
    "price": price || "0.00",
    "availability": isAvailable
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock",
    "seller": {
      "@type": "Organization",
      "name": "Feeling Froggy LLC"
    }
  };

  return baseSchema;
};

/**
 * BreadcrumbList JSON-LD Schema Generator
 */
export const createBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

/**
 * Helper function to combine keywords arrays
 */
export const combineKeywords = (...arrays) => {
  const combined = arrays.flat();
  return [...new Set(combined)]; // Deduplicate
};
